import * as React from 'react';

import { useQuery } from '@apollo/client';
import type Maybe from 'graphql/tsutils/Maybe';

import { getClientName } from 'bundles/cms/utils/SpaceUtils';
import type {
  EntWebsiteResourceCardFragment,
  EntWebsiteResourceCardsSearchQuery,
  EntWebsiteResourceCardsSearchQueryVariables,
} from 'bundles/ent-website/__generated__/contentfulTypes';
import { entWebsiteResourceCardsSearchQuery } from 'bundles/ent-website/queries/EntWebsiteResourceCardsSearchQuery';

type HookReturn = {
  loading: boolean;
  data?: Array<Maybe<{ __typename?: 'EntWebsiteResourceCard' } & EntWebsiteResourceCardFragment>>;
};

export const DEFAULT_USE_CASE_VALUE = 'All';

export type HookProps = {
  searchString: string;
  useCase: string;
  domains: string[];
  isPreview: boolean;
  contentTypes: Maybe<Maybe<string>[]>;
  listOfCardSlugsToExcludeFromSearch: Maybe<Maybe<string>[]>;
};

export function useResourceHubSearchQuery({
  searchString,
  useCase,
  domains,
  isPreview,
  contentTypes,
  listOfCardSlugsToExcludeFromSearch,
}: HookProps): HookReturn {
  const context = React.useMemo(() => ({ clientName: getClientName('ENTERPRISE', isPreview) }), [isPreview]);

  const useCases = React.useMemo(
    () => (useCase?.length > 0 && useCase !== DEFAULT_USE_CASE_VALUE ? [useCase] : undefined),
    [useCase]
  );

  const sharedVariables = React.useMemo(
    () => ({
      searchString,
      // Query all cards if domain is dev
      ...(domains.includes('dev') ? {} : { domains }),
      contentTypes: contentTypes?.map(String),
      excludeSlugs: listOfCardSlugsToExcludeFromSearch?.map(String),
      preview: isPreview,
    }),
    [contentTypes, domains, isPreview, listOfCardSlugsToExcludeFromSearch, searchString]
  );

  const withUseCaseVairables = React.useMemo(
    () => ({
      ...sharedVariables,
      // If search string is present we discard the selected useCases/topics. See Jira ENTING-1363
      includeUseCases: useCases,
    }),
    [sharedVariables, useCases]
  );

  const excludeUseCaseVairables = React.useMemo(
    () => ({
      ...sharedVariables,
      // If search string is present we discard the selected useCases/topics. See Jira ENTING-1363
      excludeUseCases: useCases,
    }),
    [sharedVariables, useCases]
  );

  const { data: dataWithUseCase, loading: loadingWithUseCase } = useQuery<
    EntWebsiteResourceCardsSearchQuery,
    EntWebsiteResourceCardsSearchQueryVariables
  >(entWebsiteResourceCardsSearchQuery, {
    context,
    variables: withUseCaseVairables,
    errorPolicy: 'all',
  });

  const { data: dataExcludingUseCase, loading: loadingExcludingUseCase } = useQuery<
    EntWebsiteResourceCardsSearchQuery,
    EntWebsiteResourceCardsSearchQueryVariables
  >(entWebsiteResourceCardsSearchQuery, {
    context,
    variables: excludeUseCaseVairables,
    errorPolicy: 'all',
  });

  const getLoading = React.useCallback(() => {
    if (searchString?.length === 0 && useCases == null) return loadingWithUseCase;
    if (searchString?.length > 0 && useCases != null) return loadingWithUseCase || loadingExcludingUseCase;
    if (searchString?.length > 0 && useCases == null) return loadingWithUseCase;
    if (searchString?.length === 0 && useCases != null) return loadingWithUseCase;

    return loadingWithUseCase || loadingExcludingUseCase;
  }, [loadingExcludingUseCase, loadingWithUseCase, searchString?.length, useCases]);

  const getData = React.useCallback(() => {
    if (searchString?.length === 0 && useCases == null)
      return dataWithUseCase?.entWebsiteResourceCardCollection?.items ?? [];
    if (searchString?.length > 0 && useCases != null)
      return [
        ...(dataWithUseCase?.entWebsiteResourceCardCollection?.items ?? []),
        ...(dataExcludingUseCase?.entWebsiteResourceCardCollection?.items ?? []),
      ];
    if (searchString?.length > 0 && useCases == null)
      return dataWithUseCase?.entWebsiteResourceCardCollection?.items ?? [];
    if (searchString?.length === 0 && useCases != null)
      return dataWithUseCase?.entWebsiteResourceCardCollection?.items ?? [];

    return [];
  }, [
    dataExcludingUseCase?.entWebsiteResourceCardCollection?.items,
    dataWithUseCase?.entWebsiteResourceCardCollection?.items,
    searchString?.length,
    useCases,
  ]);

  // 1. If none are present return withUseCases
  // 2. If both are present return wihtUseCases ++ excludeUseCases
  // 3. If only searchString is Present: return only withUseCases
  // 4. If only useCases are present: return only with withUseCases
  const result = React.useMemo(
    () => ({
      loading: getLoading(),
      data: getData(),
    }),
    [getData, getLoading]
  );

  return result;
}
